/* eslint-disable default-case */
import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';

const CustomSelect = ({
  options = [],
  value = '',
  onChange = () => {},
  placeholder = 'Select an option...',
  label = '',
  error = '',
  disabled = false,
  className = '',
  size = 'md',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const selectRef = useRef(null);

  const sizeClasses = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-3 py-2 sm:px-4 sm:py-2.5 text-sm sm:text-base',
    lg: 'px-4 py-3 text-base'
  };

  const baseClasses = 'block w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black transition-colors bg-white cursor-pointer';
  
  const inputClasses = `
    ${baseClasses}
    ${sizeClasses[size]}
    ${error ? 'border-red-300 focus:ring-red-500' : ''}
    ${disabled ? 'bg-gray-100 cursor-not-allowed opacity-50' : ''}
    ${className}
  `.trim();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleSelect = (option) => {
    const optionValue = typeof option === 'string' ? option : option.value;
    onChange(optionValue);
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (disabled) return;

    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (isOpen && highlightedIndex >= 0) {
          const filteredOptions = options;
          if (filteredOptions[highlightedIndex]) {
            handleSelect(filteredOptions[highlightedIndex]);
          }
        } else {
          setIsOpen(!isOpen);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setHighlightedIndex((prev) => 
            prev < options.length - 1 ? prev + 1 : prev
          );
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (isOpen) {
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        }
        break;
    }
  };

  const getDisplayValue = () => {
    if (!value) return placeholder;
    const option = options.find(opt => {
      const optValue = typeof opt === 'string' ? opt : opt.value;
      return optValue === value;
    });
    if (!option) return value;
    return typeof option === 'string' ? option : option.label || option.name;
  };

  const isSelected = (option) => {
    const optionValue = typeof option === 'string' ? option : option.value;
    return value === optionValue;
  };

  return (
    <div className="w-full" ref={selectRef}>
      {label && (
        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
          {label}
        </label>
      )}
      
      <div className="relative">
        <div
          className={inputClasses}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          onKeyDown={handleKeyDown}
          tabIndex={disabled ? -1 : 0}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls={isOpen ? 'custom-select-options' : undefined}
        >
          <div className="flex items-center justify-between">
            <span className={`${!value ? 'text-gray-400' : 'text-black'} truncate`}>
              {getDisplayValue()}
            </span>
            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ml-2 ${isOpen ? 'rotate-180' : ''}`} />
          </div>
        </div>

        {isOpen && (
          <div 
            id="custom-select-options"
            className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto"
            role="listbox"
          >
            <div className="py-1">
              {options.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500 text-center">
                  No options available
                </div>
              ) : (
                options.map((option, index) => {
                  const optionValue = typeof option === 'string' ? option : option.value;
                  const optionLabel = typeof option === 'string' ? option : option.label || option.name;
                  const isOptionSelected = isSelected(option);
                  const isHighlighted = index === highlightedIndex;

                  return (
                    <div
                      key={optionValue}
                      className={`px-3 py-2 cursor-pointer flex items-center justify-between text-sm sm:text-base transition-colors ${
                        isHighlighted ? 'bg-gray-100' : 'hover:bg-gray-50'
                      } ${isOptionSelected ? 'bg-black text-white' : 'text-gray-900'}`}
                      onClick={() => handleSelect(option)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                    >
                      <span>{optionLabel}</span>
                      {isOptionSelected && (
                        <Check className="w-4 h-4 flex-shrink-0 ml-2" />
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
      
      {error && (
        <p className="mt-1 text-xs sm:text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default CustomSelect;

